// IcicleView.tsx
// React component for rendering and editing the icicle graph layout

import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import type { HierarchyNode, NodeType, RelationshipType, ReparentArgs } from '../types';
import { getPairingSourceId, getPairingTargetId, relationshipToPairings } from '../utils/relationshipRules';
import { scoreToHighlightColor } from '../utils/colors';

interface IcicleViewProps {
  graphData: HierarchyNode | null;
  nodeTypes: NodeType[];
  relationshipTypes: RelationshipType[];
  setSelectedNode: (node: HierarchyNode) => void;
  reparent: (args: ReparentArgs) => Promise<void>;
  focusNodeId: string | null;
  setFocusNodeId: (id: string) => void;
  /** When false, drag-reparenting is disabled (viewer role). */
  canEdit: boolean;
  /** Highlighted nodes. The focus node is passed here so it stays visually distinct. */
  highlightedNodeScores?: Map<string, number>;
}

const IcicleView: React.FC<IcicleViewProps> = ({
  graphData,
  nodeTypes,
  relationshipTypes,
  setSelectedNode,
  reparent,
  focusNodeId,
  setFocusNodeId,
  canEdit,
  highlightedNodeScores = new Map(),
}) => {
  // Drag-and-drop state for icicle reparenting
  const [allowedDropTargets, setAllowedDropTargets] = useState<string[]>([]);

  // Refs for props/state that must be readable in stale closures (pointer event handlers)
  const nodeTypesRef = useRef<NodeType[]>(nodeTypes);
  const relationshipTypesRef = useRef<RelationshipType[]>(relationshipTypes);
  const rootHierarchyRef = useRef<d3.HierarchyNode<HierarchyNode> | null>(null);

  useEffect(() => { nodeTypesRef.current = nodeTypes; }, [nodeTypes]);
  useEffect(() => { relationshipTypesRef.current = relationshipTypes; }, [relationshipTypes]);

  // Helper: get valid parent options for a node (uses backend relationship rules)
  // NOTE: reads from refs so this is safe to call from stale pointer-event closures
  const getValidParentOptions = (childNode: d3.HierarchyNode<HierarchyNode>): string[] => {
    const currentRelTypes = relationshipTypesRef.current;
    const currentNodeTypes = nodeTypesRef.current;
    const currentRoot = rootHierarchyRef.current;

    if (
      !childNode ||
      !childNode.data ||
      !childNode.data.type ||
      !Array.isArray(currentRelTypes) ||
      !currentRelTypes.length ||
      !Array.isArray(currentNodeTypes) ||
      !currentNodeTypes.length
    ) {
      return [];
    }

    const validParents = new Set<string>();
    currentRelTypes.forEach((rel) => {
      relationshipToPairings(rel).forEach((pairing) => {
        if (getPairingSourceId(pairing) === childNode.data.type) {
          if (currentRoot) {
            currentRoot.descendants().forEach((n) => {
              if (n.data.type === getPairingTargetId(pairing) && n.data.id !== childNode.data.id) {
                validParents.add(n.data.id);
              }
            });
          }
        }
      });
    });

    // Exclude descendants, self, and current parent to prevent cycles
    const descendants = new Set<string>();
    const collectDescendants = (node: d3.HierarchyNode<HierarchyNode>) => {
      descendants.add(node.data.id);
      if (node.children) node.children.forEach(collectDescendants);
    };
    collectDescendants(childNode);

    let parentId: string | null = null;
    if (childNode.parent && childNode.parent.data && childNode.parent.data.id) {
      parentId = childNode.parent.data.id;
    }

    return Array.from(validParents).filter(
      (id) => !descendants.has(id) && id !== parentId
    );
  };

  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(800);
  const [focusNode, setFocusNode] = useState<d3.HierarchyNode<HierarchyNode> | null>(null);
  const [rootHierarchy, setRootHierarchy] = useState<d3.HierarchyNode<HierarchyNode> | null>(null);
  // Keep rootHierarchyRef in sync for stale-closure-safe access
  useEffect(() => { rootHierarchyRef.current = rootHierarchy; }, [rootHierarchy]);

  // Hover-expand state
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleHoverEnter = (nodeId: string): void => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHoveredNodeId(nodeId), 200);
  };

  const handleHoverLeave = (): void => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = null;
    setHoveredNodeId(null);
  };

  // Clean up hover timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  let visibleNodes: d3.HierarchyRectangularNode<HierarchyNode>[] = [];

  // Build color scale from unique node types
  let color: d3.ScaleOrdinal<string, string> | null = null;
  const typeSet = new Set<string>();
  if (rootHierarchy) {
    rootHierarchy.descendants().forEach((n) => {
      if (n.data.type) typeSet.add(n.data.type);
    });
  }
  const typeArray = Array.from(typeSet);
  if (typeArray.length > 0) {
    color = d3
      .scaleOrdinal<string, string>()
      .domain(typeArray)
      .range(d3.quantize(d3.interpolateRainbow, typeArray.length));
  }

  // Build hierarchy and store root, restore focusNode from focusNodeId if possible
  useEffect(() => {
    if (!graphData) return;
    const root = d3
      .hierarchy<HierarchyNode>(graphData)
      .sum((d) => (d as any).value || 1)
      .sort((a, b) => ((b as any).value || 1) - ((a as any).value || 1));
    setRootHierarchy(root);

    let nextFocus: d3.HierarchyNode<HierarchyNode> = root;
    let found: d3.HierarchyNode<HierarchyNode> | undefined;
    if (focusNodeId) {
      found = root.descendants().find((n) => n.data.id === focusNodeId);
      if (found) nextFocus = found;
    }
    setFocusNode(nextFocus);
    if (focusNodeId && !found) {
      setFocusNodeId(root.data.id);
    }
  }, [graphData, focusNodeId, setFocusNodeId]);

  // Helper to prune hierarchy to a max depth (operates on raw HierarchyNode data)
  function pruneToDepth(node: HierarchyNode, depth: number): HierarchyNode {
    const childrenArr = Array.isArray(node.children) ? node.children : [];
    if (depth === 0 || childrenArr.length === 0) {
      return { ...node, children: undefined };
    }
    return {
      ...node,
      children: childrenArr.map((child) => pruneToDepth(child, depth - 1)),
    };
  }

  // Log focusNode changes
  useEffect(() => {
    if (focusNode && focusNode.data && focusNode.data.id) {
      console.log('[IcicleView] focusNode changed:', focusNode.data.id);
    }
  }, [focusNode]);

  let svgHeight = containerHeight || 800;
  if (rootHierarchy && focusNode) {
    const width = 1200;
    const visibleDepth = 4;
    const MIN_NODE_HEIGHT = 30;

    const prunedRoot = pruneToDepth(focusNode.data, visibleDepth);
    const focusSubtree = d3
      .hierarchy<HierarchyNode>(prunedRoot)
      .sum((d) => (d as any).value || 1)
      .sort((a, b) => ((b as any).value || 1) - ((a as any).value || 1));

    const leafCount = focusSubtree.leaves().length;
    svgHeight = Math.max(containerHeight || 800, leafCount * MIN_NODE_HEIGHT);

    const partition = d3.partition<HierarchyNode>().size([svgHeight, width]);
    partition(focusSubtree);

    visibleNodes = focusSubtree
      .descendants()
      .filter((d): d is d3.HierarchyRectangularNode<HierarchyNode> => d.depth <= visibleDepth);
  }

  // Resize observer for scroll wrapper (excludes breadcrumb height)
  useEffect(() => {
    if (!scrollRef.current) return;
    const handleResize = (): void => {
      if (scrollRef.current) {
        setContainerHeight(scrollRef.current.offsetHeight);
      }
    };
    handleResize();
    let observer: ResizeObserver | undefined;
    if (window.ResizeObserver) {
      observer = new ResizeObserver(() => handleResize());
      observer.observe(scrollRef.current!);
    } else {
      window.addEventListener('resize', handleResize);
    }
    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Helper to find node in rootHierarchy by id
  const findNodeById = (id: string): d3.HierarchyNode<HierarchyNode> | undefined => {
    if (!rootHierarchy) return undefined;
    return rootHierarchy.descendants().find((n) => n.data.id === id);
  };

  // Drag-and-drop state
  const [draggedNode, setDraggedNode] = useState<d3.HierarchyNode<HierarchyNode> | null>(null);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const draggedNodeRef = useRef<d3.HierarchyNode<HierarchyNode> | null>(null);
  const dragPositionRef = useRef<{ x: number; y: number } | null>(null);
  const allowedDropTargetsRef = useRef<string[]>([]);

  useEffect(() => {
    draggedNodeRef.current = draggedNode;
  }, [draggedNode]);
  useEffect(() => {
    dragPositionRef.current = dragPosition;
  }, [dragPosition]);
  useEffect(() => {
    allowedDropTargetsRef.current = allowedDropTargets;
  }, [allowedDropTargets]);

  const handleDragStart = (
    node: d3.HierarchyNode<HierarchyNode>,
    pointerEvent: { clientX: number; clientY: number }
  ): void => {
    setDraggedNode(node);
    draggedNodeRef.current = node; // sync update so early pointermove events are not skipped
    const allowed = getValidParentOptions(node);
    setAllowedDropTargets(allowed);
    allowedDropTargetsRef.current = allowed;
    if (pointerEvent && svgRef.current) {
      const svgRect = svgRef.current.getBoundingClientRect();
      const pos = {
        x: pointerEvent.clientX - svgRect.left,
        y: pointerEvent.clientY - svgRect.top,
      };
      setDragPosition(pos);
      dragPositionRef.current = pos;
    }
  };

  const handleDragMove = (moveEvent: { clientX: number; clientY: number }): void => {
    if (!draggedNodeRef.current || !svgRef.current) return;
    const svgRect = svgRef.current.getBoundingClientRect();
    const pos = {
      x: moveEvent.clientX - svgRect.left,
      y: moveEvent.clientY - svgRect.top,
    };
    setDragPosition(pos);
    dragPositionRef.current = pos;
  };

  const handleDragEnd = async (): Promise<void> => {
    const node = draggedNodeRef.current;
    const pos = dragPositionRef.current;
    const allowed = allowedDropTargetsRef.current;
    if (!node || !pos) {
      setAllowedDropTargets([]);
      setDraggedNode(null);
      setDragPosition(null);
      dragPositionRef.current = null;
      allowedDropTargetsRef.current = [];
      return;
    }
    const mouseX = pos.x;
    const mouseY = pos.y;
    const dropTarget = visibleNodes.find((n) => {
      const x0 = n.y0,
        x1 = n.y1,
        y0 = n.x0,
        y1 = n.x1;
      return mouseX >= x0 && mouseX <= x1 && mouseY >= y0 && mouseY <= y1;
    });

    if (dropTarget && allowed.includes(dropTarget.data.id)) {
      await reparent({
        childNode: node.data,
        oldParent: node.parent ? node.parent.data : null,
        newParent: dropTarget.data,
      });
    }
    setAllowedDropTargets([]);
    setDraggedNode(null);
    setDragPosition(null);
    dragPositionRef.current = null;
    allowedDropTargetsRef.current = [];
  };

  // Breadcrumb rendering
  const renderBreadcrumb = (): React.ReactNode => {
    if (!focusNode) return null;
    const ancestors = focusNode.ancestors().reverse();
    return (
      <div style={{ padding: '4px 0 4px 8px', fontSize: 14, background: '#f5f5f5' }}>
        {ancestors.map((d, i) => (
          <span
            key={d.data.id || i}
            style={{
              cursor: i < ancestors.length - 1 ? 'pointer' : 'default',
              color: i < ancestors.length - 1 ? '#1976d2' : '#222',
            }}
            onClick={() => {
              if (i < ancestors.length - 1) setFocusNode(d);
            }}
          >
            {d.data.name || d.data.id}
            {i < ancestors.length - 1 && <span style={{ margin: '0 4px' }}>/</span>}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', width: '100%', height: '100%', minHeight: 300, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {renderBreadcrumb()}
      <div ref={scrollRef} style={{ width: '100%', flex: 1, minHeight: 0, overflow: 'auto' }}>
        <svg
          ref={svgRef}
          className="icicle-graph"
          style={{ width: '100%', height: svgHeight, background: '#f9f9f9' }}
          role="tree"
          tabIndex={0}
          aria-label="Icicle Graph"
        >
          {[...visibleNodes]
            .sort((a, b) => {
              if (a.data.id === hoveredNodeId) return 1;
              if (b.data.id === hoveredNodeId) return -1;
              return 0;
            })
            .map((d, i) => {
              const rectX = d.y0;
              const rectY = d.x0;
              const rectW = d.y1 - d.y0;
              const naturalH = d.x1 - d.x0;
              const label = d.data.name || d.data.description || d.data.id;
              const description = d.data.description;
              const isHovered = d.data.id === hoveredNodeId;
              const MIN_EXPANDED_H = 120;
              const rectH =
                isHovered && description ? Math.max(naturalH, MIN_EXPANDED_H) : naturalH;
              const showDescription = description && rectH - 32 > 12;

              const fillColor = (() => {
                if (!color) return '#ccc';
                const nodeType = d.data.type;
                if (!nodeType || !typeSet.has(nodeType)) return '#ccc';
                const highlightScore = highlightedNodeScores.get(d.data.id);
                if (highlightScore !== undefined) return scoreToHighlightColor(highlightScore);
                return color(nodeType);
              })();

              const getLuminance = (hex: string): number => {
                const c = d3.color(hex);
                if (!c) return 1;
                const r = (c as d3.RGBColor).r / 255,
                  g = (c as d3.RGBColor).g / 255,
                  b = (c as d3.RGBColor).b / 255;
                const toLinear = (v: number): number =>
                  v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
                return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
              };

              const textColor = getLuminance(fillColor) < 0.4 ? '#fff' : '#222';
              const descColor =
                getLuminance(fillColor) < 0.4 ? 'rgba(255,255,255,0.85)' : '#444';

              return (
                <g key={d.data.id || i}>
                  <rect
                    x={rectX}
                    y={rectY}
                    width={rectW}
                    height={rectH}
                    fill={fillColor}
                    opacity={1}
                    data-node-id={d.data.id}
                    style={{
                      cursor: d.children ? 'pointer' : 'default',
                      strokeWidth: isHovered ? 2 : 4,
                      stroke: allowedDropTargetsRef.current.includes(d.data.id)
                        ? '#1976d2'
                        : isHovered
                        ? '#333'
                        : '#fff',
                      filter:
                        isHovered && description
                          ? 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))'
                          : 'none',
                    }}
                    onMouseEnter={() => handleHoverEnter(d.data.id)}
                    onMouseLeave={() => handleHoverLeave()}
                    onClick={(e) => {
                      e.stopPropagation();
                      const nodeInHierarchy = findNodeById(d.data.id);
                      if (!nodeInHierarchy) return;
                      let newFocus: d3.HierarchyNode<HierarchyNode>;
                      if (
                        focusNode &&
                        focusNode.data.id === nodeInHierarchy.data.id &&
                        nodeInHierarchy.parent
                      ) {
                        newFocus = nodeInHierarchy.parent;
                      } else {
                        newFocus = nodeInHierarchy;
                      }
                      setFocusNode(newFocus);
                      setFocusNodeId(newFocus.data.id);
                      
                      setSelectedNode(d.data);
                    }}
                    onPointerDown={(e) => {
                      if (e.button !== 0) return;
                      if (!canEdit) return; // viewers cannot reparent
                      e.preventDefault();
                      e.stopPropagation();
                      handleDragStart(d, e);
                      const moveHandler = (moveEvent: PointerEvent) => {
                        moveEvent.preventDefault();
                        handleDragMove(moveEvent);
                      };
                      const upHandler = (upEvent: PointerEvent) => {
                        // use the exact cursor position at release time
                        if (svgRef.current) {
                          const svgRect = svgRef.current.getBoundingClientRect();
                          dragPositionRef.current = {
                            x: upEvent.clientX - svgRect.left,
                            y: upEvent.clientY - svgRect.top,
                          };
                        }
                        handleDragEnd();
                        window.removeEventListener('pointermove', moveHandler);
                        window.removeEventListener('pointerup', upHandler);
                      };
                      window.addEventListener('pointermove', moveHandler);
                      window.addEventListener('pointerup', upHandler);
                    }}
                  />
                  {(isHovered || naturalH > 20) && (
                    <text
                      x={rectX + 8}
                      y={rectY + 8}
                      fontSize={15}
                      fontWeight="bold"
                      fill={textColor}
                      style={{ pointerEvents: 'none' }}
                      textAnchor="start"
                      dominantBaseline="hanging"
                    >
                      <tspan
                        style={{
                          display: 'inline-block',
                          maxWidth: rectW - 16 > 0 ? rectW - 16 : 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label}
                      </tspan>
                    </text>
                  )}
                  {showDescription && (
                    <text
                      x={rectX + 8}
                      y={rectY + 32}
                      fontSize={12}
                      fill={descColor}
                      style={{ pointerEvents: 'none' }}
                      textAnchor="start"
                      dominantBaseline="hanging"
                    >
                      {(() => {
                        const maxChars = Math.floor(rectW / 7);
                        if (!description) return null;
                        const words = description.split(' ');
                        const lines: string[] = [];
                        let line = '';
                        words.forEach((word) => {
                          if ((line + word).length > maxChars) {
                            lines.push(line.trim());
                            line = word + ' ';
                          } else {
                            line += word + ' ';
                          }
                        });
                        if (line.trim()) lines.push(line.trim());
                        const availableHeight = rectH - 32;
                        const lineHeight = 16;
                        const maxLines = Math.floor(availableHeight / lineHeight);
                        let truncated = false;
                        let displayLines = lines;
                        if (lines.length > maxLines) {
                          displayLines = lines.slice(0, maxLines);
                          truncated = true;
                        }
                        return displayLines.map((l, idx) => (
                          <tspan x={rectX + 8} dy={idx === 0 ? 0 : 16} key={idx}>
                            {truncated && idx === displayLines.length - 1
                              ? l.replace(/\s+$/, '') + '…'
                              : l}
                          </tspan>
                        ));
                      })()}
                    </text>
                  )}
                </g>
              );
            })}
        </svg>
      </div>

      {/* Drag preview overlay */}
      {draggedNode && dragPosition && (
        <div
          style={{
            position: 'absolute',
            left: dragPosition.x - 60,
            top: dragPosition.y - 20,
            width: 120,
            height: 40,
            pointerEvents: 'none',
            zIndex: 20,
          }}
        >
          <svg width={120} height={40} style={{ overflow: 'visible' }}>
            <rect
              x={0}
              y={0}
              width={120}
              height={40}
              fill="#1976d2"
              opacity={0.8}
              rx={8}
              ry={8}
              style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.2))' }}
            />
            <text
              x={60}
              y={20}
              fontSize={16}
              fontWeight="bold"
              fill="#fff"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ pointerEvents: 'none' }}
            >
              {draggedNode.data.id}
            </text>
          </svg>
        </div>
      )}
    </div>
  );
};

export default IcicleView;
